import type React from 'react';
import { useEffect } from 'react';

import { Spacer, ToggleButton } from '@zillow/constellation';

import type { SwitchObject } from '../../../models/Types';
import { SMOKING_POLICY } from '../../../constants';
import type { Section } from '../../../models';
import { SmokingPolicySection } from '../../../models';
import SwitchesWithTitle from '../../Widgets/SwitchesWithTitle';
import SmokingAdditionalAreas from './Smoking/SmokingAdditionalAreas';
import SmokingButtonGroup from './Smoking/SmokingButtonGroup';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: SmokingPolicySection) => void;
  children?: React.ReactNode;
  outsideTextOverride?: string;
};

export default function SmokingPolicy({
  section,
  setSection,
  setSectionValues,
  children,
  outsideTextOverride,
}: Props): JSX.Element {
  useEffect(() => {
    const smokingPolicySection = new SmokingPolicySection(section.initialParams);
    setSection(smokingPolicySection);
  }, []);

  function onRemoveAll(): void {
    section.removeAllIndexes();
    setSection(section);
  }

  function onNoSmokingClick(): void {
    setSectionValues({
      [SMOKING_POLICY.FIELD_NAME.NO_SMOKING]: true,
      [SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS]: false,
      [SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY]: false,
    });
    onRemoveAll();
  }

  function onCertainAreasClick(): void {
    setSectionValues({
      [SMOKING_POLICY.FIELD_NAME.NO_SMOKING]: false,
      [SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS]: true,
      [SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY]: false,
    });
  }

  function onAllowedThroughoutPropertyClick(): void {
    setSectionValues({
      [SMOKING_POLICY.FIELD_NAME.NO_SMOKING]: false,
      [SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS]: false,
      [SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY]: true,
    });
    onRemoveAll();
  }

  function onInsideUnitToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.INSIDE_UNIT);
  }

  function onCommonIndoorAreasToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.COMMON_INDOOR_AREAS);
  }

  function onOutsideMoreThan10FeetToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.OUTSIDE_MORE_THAN_10_FEET);
  }

  function onPorchesPatiosYardsToggle(): void {
    toggleSwitch(SMOKING_POLICY.FIELD_NAME.PORCHES_PATIOS_YARDS);
  }

  function toggleSwitch(key: string): void {
    setSectionValues({
      [key]: !section.getFieldValue(key),
    });
  }

  const isNoSmoking = section.getFieldValue(SMOKING_POLICY.FIELD_NAME.NO_SMOKING);
  const isCertainAreas = section.getFieldValue(SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS);
  const isAllowedThroughoutProperty = section.getFieldValue(SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY);
  const smokingPolicies: Array<SwitchObject> = [
    {
      text: 'Inside the unit',
      onClick: onInsideUnitToggle,
      toggled: section.getFieldValue(SMOKING_POLICY.FIELD_NAME.INSIDE_UNIT),
      disabled: section.isFieldDisabled(SMOKING_POLICY.FIELD_NAME.INSIDE_UNIT),
    },
    {
      text: 'Common indoor areas',
      onClick: onCommonIndoorAreasToggle,
      toggled: section.getFieldValue(SMOKING_POLICY.FIELD_NAME.COMMON_INDOOR_AREAS),
      disabled: section.isFieldDisabled(SMOKING_POLICY.FIELD_NAME.COMMON_INDOOR_AREAS),
    },
    {
      text: outsideTextOverride || 'Outside, more than 10 feet from building',
      onClick: onOutsideMoreThan10FeetToggle,
      toggled: section.getFieldValue(SMOKING_POLICY.FIELD_NAME.OUTSIDE_MORE_THAN_10_FEET),
      disabled: section.isFieldDisabled(SMOKING_POLICY.FIELD_NAME.OUTSIDE_MORE_THAN_10_FEET),
    },
    {
      text: 'Porches, patios, and yards adjacent to units',
      onClick: onPorchesPatiosYardsToggle,
      toggled: section.getFieldValue(SMOKING_POLICY.FIELD_NAME.PORCHES_PATIOS_YARDS),
      disabled: section.isFieldDisabled(SMOKING_POLICY.FIELD_NAME.PORCHES_PATIOS_YARDS),
    },
  ];

  return (
    <>
      <SmokingButtonGroup>
        <ToggleButton
          data-testid="no-smoking"
          disabled={section.isFieldDisabled(SMOKING_POLICY.FIELD_NAME.NO_SMOKING)}
          onClick={onNoSmokingClick}
          pressed={isNoSmoking}
        >
          No, smoking not allowed
        </ToggleButton>
        <ToggleButton
          data-test="certain-areas"
          disabled={section.isFieldDisabled(SMOKING_POLICY.FIELD_NAME.CERTAIN_AREAS)}
          onClick={onCertainAreasClick}
          pressed={isCertainAreas}
        >
          Yes, allowed in certain areas
        </ToggleButton>
        <ToggleButton
          data-test="throughout-property"
          disabled={section.isFieldDisabled(SMOKING_POLICY.FIELD_NAME.ALLOWED_THROUGHOUT_PROPERTY)}
          onClick={onAllowedThroughoutPropertyClick}
          pressed={isAllowedThroughoutProperty}
        >
          Yes, allowed throughout property
        </ToggleButton>
      </SmokingButtonGroup>
      {isCertainAreas && (
        <Spacer marginTop="sm">
          <SwitchesWithTitle switches={smokingPolicies} legend="Where is smoking allowed?" noBottomMargin />
          <SmokingAdditionalAreas section={section} setSection={setSection} setSectionValues={setSectionValues} />
        </Spacer>
      )}
      {children}
    </>
  );
}
